import React, { useEffect, useMemo, useState } from "react";
import { useQuery as useConvexQuery } from "convex/react";
import { refs } from "../../convex/apiRefs";
import { decodeConvexDoc } from "../../convex/decodeConvexDoc";
import { applyClientFilter } from "../../utility/compassClientFilter";
import CompassResultsTable from "./CompassResultsTable";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Compass (#191) — Convex backend browser.
//
// Reads the Convex mirror reactively (live!) via compass:browse. Convex
// paginates by opaque cursor (no random offset), so we walk it with a cursor
// stack and support First / Prev / Next / Last where Prev/Next always move in
// the same ABSOLUTE direction (toward page 1 / toward the last page) in both
// modes:
//   - HEAD mode (order "asc"):  page 1, 2, 3 … via continueCursor.
//   - TAIL mode (order "desc"): "Last" jumps here showing the final page (rows
//       reversed for display). From the tail, Prev walks toward page 1 by
//       fetching successive desc pages; Next walks back toward the end.
//
// The total record count comes from syncMeta (sync:stats) — Convex can't
// cheaply count an arbitrary filter, so the total + "of N pages" show only
// when NO search/filter is narrowing the set.
//
// Server-side search is only possible on INDEXED columns (convexSearchField).
// Everything else — the raw filter box, or a quick-search on a non-indexed
// field — is applied to the loaded page client-side via applyClientFilter.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

function CompassConvexBrowser({
  source,
  table,
  convexSearchField,
  convexSearchValue,
  clientFilter,
  pageSize,
  previewFields,
  selectedId,
  onSelect,
}) {
  const [order, setOrder] = useState("asc"); // "asc" = head, "desc" = tail
  const [cursorStack, setCursorStack] = useState([null]);
  const [idx, setIdx] = useState(0);

  // Reset paging when the query inputs change.
  useEffect(() => {
    setOrder("asc");
    setCursorStack([null]);
    setIdx(0);
  }, [source, table, convexSearchField, convexSearchValue, pageSize, clientFilter]);

  const cursor = cursorStack[idx] ?? null;

  const args = useMemo(() => {
    const a = { source, table, cursor, pageSize, order };
    if (convexSearchField && convexSearchValue) {
      a.searchField = convexSearchField;
      a.searchValue = convexSearchValue;
    }
    return a;
  }, [source, table, cursor, pageSize, order, convexSearchField, convexSearchValue]);

  const result = useConvexQuery(refs.compassBrowse, args);
  const loading = result === undefined;

  // Whole-collection total (from the last sync). Only meaningful when nothing
  // is narrowing the result set.
  const stats = useConvexQuery(refs.compassStats, { source });
  const filtering =
    Boolean(convexSearchField && convexSearchValue) ||
    Boolean(clientFilter && Object.keys(clientFilter).length > 0);
  const total =
    stats && typeof stats[table] === "number" ? stats[table] : null;
  const totalPages =
    !filtering && total != null
      ? Math.max(1, Math.ceil(total / pageSize))
      : null;

  const { docs, unindexed } = useMemo(() => {
    if (!result) return { docs: [], unindexed: false };
    let rows = (result.page || []).map((r) => decodeConvexDoc(r.data));
    if (order === "desc") rows = [...rows].reverse();
    rows = applyClientFilter(rows, clientFilter || {});
    return { docs: rows, unindexed: Boolean(result.unindexedSearch) };
  }, [result, order, clientFilter]);

  // Never leave the document pane empty when rows exist: auto-select the first
  // row whenever the displayed page changes and the current selection isn't in
  // it. Manual clicks on a still-present row are preserved.
  useEffect(() => {
    if (!docs.length || !onSelect) return;
    const stillShown = selectedId != null && docs.some((d) => d._id === selectedId);
    if (!stillShown) onSelect(docs[0]);
  }, [docs, selectedId, onSelect]);

  const isDone = result?.isDone ?? true;
  const continueCursor = result?.continueCursor ?? null;

  // - - - cursor stepping primitives
  const stepForward = () => {
    // advance one page in the CURRENT order direction
    if (isDone) return;
    setCursorStack((stack) => {
      const next = stack.slice(0, idx + 1);
      next.push(continueCursor);
      return next;
    });
    setIdx((i) => i + 1);
  };
  const stepBack = () => setIdx((i) => Math.max(0, i - 1));

  // - - - absolute-direction nav (works in both modes)
  const goFirst = () => {
    setOrder("asc");
    setCursorStack([null]);
    setIdx(0);
  };
  const goLast = () => {
    setOrder("desc");
    setCursorStack([null]);
    setIdx(0);
  };
  const goPrev = () => {
    // toward page 1
    if (order === "asc") stepBack();
    else stepForward(); // tail: earlier pages are further into the desc walk
  };
  const goNext = () => {
    // toward the last page
    if (order === "asc") stepForward();
    else stepBack();
  };

  // enable/disable + page number
  const prevEnabled = order === "asc" ? idx > 0 : !isDone;
  const nextEnabled = order === "asc" ? !isDone : idx > 0;
  const firstEnabled = !(order === "asc" && idx === 0);
  const lastEnabled = !(order === "desc" && idx === 0);
  const pageNum =
    order === "asc" ? idx + 1 : totalPages != null ? totalPages - idx : null;

  const btn =
    "px-2 py-1 border-2 border-skin-base rounded disabled:opacity-40";

  return (
    <div className="flex flex-col h-full min-h-0">
      {unindexed && (
        <div className="text-xs text-red mb-1">
          That field isn’t indexed in Convex — showing the page and filtering
          it locally. Use the MongoDB backend for a full server-side search.
        </div>
      )}
      <div className="flex-1 min-h-0">
        <CompassResultsTable
          docs={docs}
          previewFields={previewFields}
          loading={loading}
          error={null}
          onSelect={onSelect}
          selectedId={selectedId}
        />
      </div>

      <div className="flex items-center gap-2 pt-2 text-xs flex-wrap">
        <button className={btn} onClick={goFirst} disabled={!firstEnabled}>
          |◀ First
        </button>
        <button className={btn} onClick={goPrev} disabled={!prevEnabled}>
          ◀ Prev
        </button>
        <span className="px-1">
          {pageNum != null ? `Page ${pageNum.toLocaleString()}` : "Page —"}
          {totalPages != null ? ` of ${totalPages.toLocaleString()}` : ""}
        </span>
        <button className={btn} onClick={goNext} disabled={!nextEnabled}>
          Next ▶
        </button>
        <button className={btn} onClick={goLast} disabled={!lastEnabled}>
          Last ▶|
        </button>
        <span className="ml-auto text-skin-link">
          {total != null && !filtering
            ? `${total.toLocaleString()} record${total === 1 ? "" : "s"} · `
            : ""}
          {docs.length} on this page
          {filtering ? " (filtered)" : ""}
          {loading ? " · loading…" : ""} · live
        </span>
      </div>
    </div>
  );
}

export default CompassConvexBrowser;
